import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { axiosGetCall } from 'src/services/apisCall';
import ReportInfo from './ReportInfo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const columns = [
  { id: 'roomId', label: 'Room ID', align: 'start' },
  { id: 'title', label: 'Title', align: 'center' },
  { id: 'startDate', label: 'Start Date', align: 'center' },
  { id: 'endDate', label: 'End Date', align: 'center' },
  { id: 'duration', label: 'Meeting Duration', align: 'center' },
  { id: 'createdBy', label: 'CreatedBy', align: 'center' },
  { id: 'recordings', label: 'Recordings', align: 'center' },
  { id: 'more', label: 'More', align: 'center' },
];

const formatDate = (dateString) => {
  const date = dayjs(dateString);
  return date.isValid() ? date.format('MM-DD-YYYY') : '-';
};

const extractCleanTitle = (title = '') => {
  if (!title) return '-';
  return title.replace(/\s*[-–]\s*\d{4}-\d{2}-\d{2}T.*$/, '').trim();
};

const ReportMeetTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedRoomInfo, setSelectedRoomInfo] = useState({
    roomId: '',
    startDate: '',
    endDate: '',
  });
  const [clearFilter, setClearFilter] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const fetchReportData = async (page, limit, startDate, endDate) => {
    setLoading(true);
    try {
      const skip = page * limit;
      const params = { skip, limit };
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const { result = [], total = 0 } = await axiosGetCall('webinarMeetReport', params);

      const formattedRows = result?.map((doc, index) => ({
        id: `${doc.roomId || index}`,
        roomId: doc.roomId || '-',
        title: extractCleanTitle(doc.title),
        startDate: formatDate(doc.startDate),
        endDate: formatDate(doc.endDate),
        duration: `${isNaN(doc.duration) || doc.duration == null ? 0 : doc.duration} M`,
        createdBy: doc.createdBy || '-',
        recordings: doc.recordings || [],
        rawStartDate: doc.startDate,
        rawEndDate: doc.endDate,
      }));

      setRows(formattedRows);
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching report meet data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(page, rowsPerPage, dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleInfoOpen = (roomId, startDate, endDate) => {
    setSelectedRoomInfo({ roomId, startDate, endDate });
    setInfoDialogOpen(true);
  };

  const handleInfoClose = () => {
    setInfoDialogOpen(false);
    setSelectedRoomInfo({
      roomId: '',
      startDate: '',
      endDate: '',
    });
  };

  const handleApplyFilter = () => {
    setClearFilter(true);
    fetchReportData(0, rowsPerPage, dateRange.startDate, dateRange.endDate);
    setPage(0);
  };

  const handleClearFilter = () => {
    setClearFilter(false);
    setDateRange({ startDate: null, endDate: null });
    fetchReportData(0, rowsPerPage, null, null);
    setPage(0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 2,
            mx: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, startDate: newValue }))}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 140,
                    '.MuiInputBase-input': { p: 1 },
                    '& label': {
                      zIndex: 0,
                    },
                  },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, endDate: newValue }))}
              minDate={dateRange.startDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 140,
                    '.MuiInputBase-input': { p: 1 },
                    '& label': {
                      zIndex: 0,
                    },
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              disabled={!dateRange.startDate && !dateRange.endDate}
              sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
            >
              Apply Filter
            </Button>
            {clearFilter && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilter}
                sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
              >
                Clear Filter
              </Button>
            )}
          </Box>

          <Box sx={{ fontWeight: 'bold', mt: { xs: 2, sm: 0 } }}>Total: {totalCount}</Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 300px)',
                position: 'relative',
              }}
            >
              <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                  <TableRow>
                    {columns?.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{ width: `${100 / columns.length}%`, fontWeight: 'bold', padding: '8px' }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ height: '300px', padding: 0 }}>
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            color: 'black',
                            fontStyle: 'italic',
                            fontSize: '1.5rem',
                          }}
                        >
                          Data not found
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows?.map((row) => (
                      <TableRow key={row.id}>
                        {columns?.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              width: `${100 / columns.length}%`,
                              padding: '8px 20px',
                              borderBottom: '.5px dotted rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            {column.id === 'recordings' ? (
                              row.recordings.length > 0 ? (
                                row.recordings?.map((rec, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1, zIndex: 0 }}>
                                    <a
                                      href={rec.donwloadLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ wordBreak: 'break-all', flex: 1 }}
                                    >
                                      <Tooltip title="Copy link">
                                        <Button
                                          size="small"
                                          onClick={() => handleCopy(rec.donwloadLink)}
                                          sx={{ ml: 1, minWidth: '30px', padding: '4px' }}
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </Button>
                                      </Tooltip>
                                    </a>
                                  </Box>
                                ))
                              ) : (
                                '-'
                              )
                            ) : column.id === 'more' ? (
                              <Tooltip title="More Info">
                                <Button
                                  onClick={() => handleInfoOpen(row.roomId, row.rawStartDate, row.rawEndDate)}
                                  sx={{ minWidth: 0, padding: 0 }}
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                            ) : (
                              row[column.id]
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
        <ReportInfo
          open={infoDialogOpen}
          onClose={handleInfoClose}
          roomId={selectedRoomInfo.roomId}
          startDate={selectedRoomInfo.startDate}
          endDate={selectedRoomInfo.endDate}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ReportMeetTable;
